import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Card from 'app/frontend/base/components/Card'
import TeamApplyViewButtons from './TeamApplyViewButtons'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { Calendar, Users } from 'lucide-react'
import GuildTeamRosterSummary from './GuildTeamRosterSummary'
import DefaultTeamStar from 'app/frontend/base/components/DefaultTeamStar'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import ItemLevelBadge from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/ItemLevelBadge'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { usePageInfo } from 'app/frontend/base/config/queries'
import dayjs from 'dayjs'
import { cn } from '@/lib/utils'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import GameVersion, {
  gameVersionHas,
} from 'app/frontend/types/custom/GameVersion'

const GuildTeamCard = ({
  team,
  roster,
  eventDays = [],
  medianItemLevel,
  raidProgress,
  href = team.path,
  teamGameVersion,
}: {
  team: wowaudit.TeamReference
  roster?: wowaudit.TeamMembership[]
  eventDays?: string[]
  medianItemLevel?: number | null
  raidProgress?: NonNullable<wowaudit.OverviewData['raidProgress']>
  href?: string
  teamGameVersion?: GameVersion
}) => {
  const { t } = useTranslation()
  const { accessLevel } = useAuth()
  const {
    data: { currentSeason },
  } = usePageInfo()
  const dayLabels = eventDays.map((day) =>
    dayjs().day(Number(day)).format('ddd'),
  )
  const eventDaysLabel = dayLabels.join(', ') || '-'
  const gameVersion = useGameVersion()
  const medianLevel = useMemo(() => {
    const levels = (roster ?? [])
      .map((member) => member.characterReference.level)
      .filter((value): value is number => !!value)
      .sort((a, b) => a - b)
    return levels.length ? levels[Math.floor(levels.length / 2)] : null
  }, [roster])
  const isOtherGameVersion =
    !!teamGameVersion && teamGameVersion !== gameVersion.id
  const showsLevel = !gameVersionHas(
    'blizzard_api',
    teamGameVersion ?? gameVersion.id,
  )

  const raidProgressSummary = raidProgress ? (
    <div className='flex items-center gap-1.5'>
      <EventAvatar
        instances={raidProgress.instances}
        className='h-5 w-5 border'
      />
      <span>
        {raidProgress.kills} / {raidProgress.total}{' '}
        {difficultiesLabels[raidProgress.difficulty]}
      </span>
    </div>
  ) : null

  const title = (
    <h3 className='font-semibold text-lg flex items-center gap-1.5 min-w-0'>
      {team.default ? <DefaultTeamStar /> : null}
      <span className='truncate'>{team.name}</span>
    </h3>
  )

  return (
    <Card className='pt-3 px-4 pb-5 flex flex-col gap-3' key={team.id}>
      <div className='flex flex-wrap items-start justify-between gap-x-6 gap-y-3'>
        {accessLevel === 'none' ? (
          title
        ) : (
          <Link to={href} className='min-w-0'>
            {title}
          </Link>
        )}

        <div className='flex flex-wrap items-center justify-end gap-x-6 gap-y-2 whitespace-nowrap'>
          <div className='flex flex-wrap items-center gap-x-6 gap-y-2'>
            <span className='flex items-center gap-1.5 truncate'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Calendar className='h-4 w-4 shrink-0' />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Weekly events are scheduled on these days
                </TooltipContent>
              </Tooltip>
              <span className='truncate'>{eventDaysLabel}</span>
            </span>
            {raidProgress && isOtherGameVersion ? (
              raidProgressSummary
            ) : raidProgress ? (
              <Tooltip>
                <TooltipTrigger asChild>{raidProgressSummary}</TooltipTrigger>
                <TooltipContent>
                  {currentSeason.expansion} {currentSeason.name}
                </TooltipContent>
              </Tooltip>
            ) : null}
            {(showsLevel ? medianLevel : medianItemLevel) ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <ItemLevelBadge
                      details={{ maxItemLevel: medianItemLevel ?? undefined }}
                      level={medianLevel}
                      gameVersion={teamGameVersion ?? gameVersion.id}
                      className='bg-transparent border-none hover:bg-transparent'
                      size='lg'
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {showsLevel ? 'Median team level' : 'Median team item level'}
                </TooltipContent>
              </Tooltip>
            ) : null}
            <span className='flex items-center gap-1.5'>
              <Users className='h-4 w-4' />
              <span>{team.characterAmount}</span>
            </span>
          </div>

          <div className='flex items-center gap-2'>
            <TeamApplyViewButtons team={team} href={href} />
          </div>
        </div>
      </div>

      <p
        className={cn(
          team.description ? 'whitespace-pre-wrap' : 'opacity-50 italic',
          'mb-4',
        )}
      >
        {team.description || t('shared.no-description')}
      </p>

      <GuildTeamRosterSummary members={roster} />
    </Card>
  )
}

export default GuildTeamCard
